import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Autocomplete, DialogActions, DialogTitle, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import React, { ChangeEvent, SyntheticEvent, useState } from 'react';
import { toast } from 'react-toastify';

import { EmailAiSuggestionButtonFormModel } from '@/components/emailWindow/components/newEmail/components/aiSuggestionButton/components/dialog/model/EmailAiSuggestionButtonForm.model.ts';
import { CheckboxFormField } from '@/components/form/CheckboxField.comp.tsx';
import { FormFieldLabel } from '@/components/form/FormFieldLabel.comp.tsx';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

import { useGetEmailAiSuggestion } from '../../../../util/useGetEmailAiSuggestion.util';

type Props = {
  emailId: string;
  onClose: (refetch: boolean) => void;
};

export const NewEmailAiSuggestionButtonDialog: React.FC<Props> = ({ emailId, onClose }) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body.dialog.aiSuggestion');
  const { getEmailAiSuggestion, isGettingEmailAiSuggestion } = useGetEmailAiSuggestion();
  const { emailAttachmentsTypeOptions, emailContentTypeOptions } = useGetSelectOptions();
  const [data, setData] = useState<EmailAiSuggestionButtonFormModel>({
    addAttachments: [],
    addCafeRoyalInfo: false,
    contentType: emailContentTypeOptions.find(it => it.value === 'CUSTOM')!,
    instruction: '',
  });

  const isCustomTypeSelected = data.contentType.value === 'CUSTOM';

  const handleSubmit = async () => {
    if (isCustomTypeSelected && (data.instruction?.length ?? 0) < 3) {
      toast(t('toast.provideInstructions'), ToastConfig.warning);
      return;
    }
    await getEmailAiSuggestion({
      dto: {
        ...data,
        addAttachments: data.addAttachments.map(it => it.value),
        contentType: data.contentType.value,
      },
      emailId,
    });
    onClose(true);
  };

  const handleChange =
    (field: keyof EmailAiSuggestionButtonFormModel) => (e: ChangeEvent<HTMLInputElement>) => {
      setData(cur => ({
        ...cur,
        [field]: e.target.value,
      }));
    };

  const handleChangeBoolean = (_event: SyntheticEvent<Element, Event>, checked: boolean) => {
    setData(cur => ({
      ...cur,
      addCafeRoyalInfo: checked,
    }));
  };

  return (
    <Dialog disablePortal open>
      <DialogTitle />
      <DialogContent>
        <Grid container spacing={2} sx={{ rowGap: 3 }}>
          <Grid size={8}>
            <Box sx={{ position: 'relative', pt: 2 }}>
              <FormFieldLabel isRequired label={t('form.contentType')} sx={{ top: -10 }} />
              <Autocomplete
                disabled={isGettingEmailAiSuggestion}
                fullWidth
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, optionValue) => option.value === optionValue.value}
                onChange={(_event, value) => {
                  if (value) {
                    setData(cur => ({ ...cur, contentType: value }));
                  }
                }}
                options={emailContentTypeOptions}
                renderInput={params => <TextField {...params} />}
                size="small"
                value={data.contentType}
              />
            </Box>
          </Grid>
          <Grid size={4} sx={{ alignContent: 'center' }}>
            <CheckboxFormField
              isDisabled={isGettingEmailAiSuggestion}
              label={t('form.addCafeRoyalInfo')}
              labelPlacement="end"
              onChange={handleChangeBoolean}
              value={data.addCafeRoyalInfo}
            />
          </Grid>
          <Grid size={12}>
            <Box sx={{ position: 'relative', pt: 2 }}>
              <FormFieldLabel
                isRequired={false}
                label={t('form.addAttachments')}
                sx={{ top: -10 }}
              />
              <Autocomplete
                disabled={isGettingEmailAiSuggestion}
                filterSelectedOptions
                fullWidth
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, optionValue) => option.value === optionValue.value}
                multiple
                onChange={(_event, value) => {
                  setData(cur => ({ ...cur, addAttachments: value }));
                }}
                options={emailAttachmentsTypeOptions}
                renderInput={params => <TextField {...params} />}
                size="small"
                value={data.addAttachments}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Box sx={{ position: 'relative', pt: 2 }}>
              <FormFieldLabel
                isRequired={isCustomTypeSelected}
                label={t('form.instruction')}
                sx={{ top: -10 }}
              />
              <TextField
                disabled={isGettingEmailAiSuggestion}
                fullWidth
                minRows={4}
                multiline
                onChange={handleChange('instruction')}
                placeholder={t('form.instructionPlaceholder')}
                value={data.instruction}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isGettingEmailAiSuggestion}
          onClick={() => onClose(false)}
          variant="outlined"
        >
          {t('button.cancel')}
        </Button>
        <Button
          endIcon={<AutoAwesomeOutlinedIcon />}
          loading={isGettingEmailAiSuggestion}
          onClick={handleSubmit}
        >
          {t('button.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
